"use client";

import Image from "next/image";
import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import GalleryViewer, { type GalleryImage } from "@/components/GalleryViewer";
import type { GalleryPhoto, GallerySection } from "@/lib/navigation";

const DEFAULT_SUB_TITLE = "Make Your Tour More Pleasure";
const DEFAULT_TITLE = "Recent Gallery";

/**
 * Home "Recent Gallery" — opens the shared, React-based {@link GalleryViewer}
 * lightbox on click, replacing the theme's jQuery `.popup-image` Magnific Popup
 * (which only bound on the initial page load, so after a client-side navigation
 * the thumbnails fell back to navigating straight to the raw image URL).
 *
 * The 7-photo, 5-column masonry layout and per-slot image dimensions mirror the
 * original template markup exactly.
 */

// Each entry is a column; the numbers are indices into the photos array.
const COLUMNS: number[][] = [[0], [1, 2], [3], [4, 5], [6]];

// Per-slot image dimensions, matching the original markup. Slot 3 is the tall
// portrait tile with a fixed height; the rest keep their intrinsic ratio.
const DIMS: Record<
  number,
  { width: number; height: number; style: CSSProperties }
> = {
  0: { width: 742, height: 696, style: { width: "100%", height: "auto" } },
  1: { width: 743, height: 575, style: { width: "100%", height: "auto" } },
  2: { width: 743, height: 575, style: { width: "100%", height: "auto" } },
  3: { width: 743, height: 1200, style: { width: "100%", height: "400px" } },
  4: { width: 742, height: 575, style: { width: "100%", height: "auto" } },
  5: { width: 742, height: 575, style: { width: "100%", height: "auto" } },
  6: { width: 742, height: 696, style: { width: "100%", height: "auto" } },
};

function GalleryTile({
  photo,
  index,
  onOpen,
}: {
  photo: GalleryPhoto;
  index: number;
  onOpen: (i: number) => void;
}) {
  const dim = DIMS[index];
  return (
    <div className="gallery-card">
      <div className="box-img global-img">
        <button
          type="button"
          onClick={() => onOpen(index)}
          aria-label={`Open ${photo.title || `image ${index + 1}`} in gallery viewer`}
          style={{
            display: "block",
            width: "100%",
            padding: 0,
            border: 0,
            background: "none",
            cursor: "zoom-in",
          }}
        >
          <div className="icon-btn">
            <i className="fal fa-magnifying-glass-plus"></i>
          </div>
          <Image
            src={photo.url}
            alt={photo.title || "gallery image"}
            width={dim.width}
            height={dim.height}
            style={dim.style}
            unoptimized
          />
        </button>
      </div>
    </div>
  );
}

export default function HomeGallery({
  photos,
  section,
}: {
  photos: GalleryPhoto[];
  section?: GallerySection;
}) {
  const subTitle = section?.subTitle || DEFAULT_SUB_TITLE;
  const title = section?.title || DEFAULT_TITLE;
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const images = useMemo<GalleryImage[]>(
    () =>
      photos.slice(0, 7).map((p, i) => ({
        id: `${p.url}#${i}`,
        src: p.url,
        alt: p.title || "gallery image",
      })),
    [photos],
  );

  const openAt = useCallback((i: number) => {
    setIndex(i);
    setOpen(true);
  }, []);

  if (photos.length < 7) return null;

  return (
    <div className="gallery-area pb-80">
      <div className="container th-container">
        <div className="title-area text-center">
          <span className="sub-title">{subTitle}</span>
          <h2 className="sec-title">{title}</h2>
        </div>
        <div className="row gy-10 gx-10 justify-content-center align-items-center">
          {COLUMNS.map((slots, col) => (
            <div key={col} className="col-md-6 col-lg-2">
              {slots.map((slot) => (
                <GalleryTile
                  key={slot}
                  photo={photos[slot]}
                  index={slot}
                  onOpen={openAt}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <GalleryViewer
        open={open}
        images={images}
        initialIndex={index}
        onClose={() => setOpen(false)}
        title={title}
      />
    </div>
  );
}
